import { GetStaticProps, GetStaticPaths } from 'next';
import React, { useEffect } from 'react';
import Head from 'next/head';
import { useSession, signIn } from 'next-auth/client';
import TagManager from 'react-gtm-module';
import dynamic from 'next/dynamic';
import Error from './_error';
import ContentfulService from '../services/contentful';
import GetComponent from '../maps/components';
import GetComponentsContentful from '../helpers/get-components-contentful';
import styles from './slug.module.scss';
import PageTransition from '../components/global/transition/transition';
import Salesforce from '../components/global/salesforce/Salesforce';

const GooglePlaces = dynamic(
  () => import('../components/global/google-places/GooglePlaces'),
  { ssr: false }
);

type Response = {
  items: Array<any>;
  page?: any;
  route?: string;
  settings?: any;
  signInRequired?: boolean;
};
interface Props {
  data: Response;
}

const Index = ({ data }: Props) => {
  const [session, loading] = useSession();

  if (!data || !data.page || !Object.keys(data.page).length) {
    return <Error errorCode={404} />;
  }

  const settings = data.settings || [];
  const seo = settings?.seo || [];
  const robots = [];

  if (seo.noIndex) {
    robots.push('noindex');
  }
  if (seo.noFollow) {
    robots.push('nofollow');
  }

  const getContent = () => {
    if (data.signInRequired) {
      if (loading) return <></>;

      if (!session) {
        return (
          <div className={styles.slug__signIn}>
            <button
              className={styles.slug__button}
              type="button"
              onClick={() => signIn()}
              name="sign in"
            >
              Sign In
            </button>
          </div>
        );
      }
    }

    return (
      <>
        <PageTransition activated mode="in" />
        {data.items &&
          data.items.map((item: any, index: number) =>
            GetComponent(item, index)
          )}
      </>
    );
  };

  useEffect(() => {
    const { dataLayer } = data.page?.analytics || {};
    TagManager.dataLayer({ dataLayer });
  }, []);

  return (
    <div className="main">
      <Head>
        <title>{seo.title || settings.title || 'Equinox+'}</title>
        <meta name="description" content={seo.description} />
        {seo.keywords && seo.keywords.length && (
          <meta name="keywords" content={seo.keywords.join(', ')} />
        )}
        {robots.length && <meta name="robots" content={robots.join()} />}
        {data.page?.analytics?.scripts?.includes('salesforce') && (
          <Salesforce />
        )}
        {data.page?.analytics?.scripts?.includes('googlePlaces') && (
          <GooglePlaces />
        )}
      </Head>
      {getContent()}
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const pages = await ContentfulService.getPageList();
  const parseRoute = (route: string) => {
    if (!route) return [];

    let arr = route.split('/');

    if (arr && arr.length) {
      arr = arr.filter((a) => a !== null && a !== undefined && a !== '');
    }

    return arr;
  };

  const slug: string[] = [];

  const paths = [{ params: { slug } }]; // Path to /

  if (pages && pages.length) {
    pages.forEach((page: any) =>
      paths.push({ params: { slug: parseRoute(page.route) } })
    );
  }

  // { fallback: false } means other routes should 404.
  // This is 'true' bacause routes can change dynamically on Contentful
  return { paths, fallback: true };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const data: Response = {
    items: [],
    signInRequired: process.env.SITE_PASSWORD_PROTECT === 'true'
  };

  data.route = `/${
    params && params.slug ? params.slug.toString().replace(/,/g, '/') : ''
  }`;

  data.page = (await ContentfulService.getPage(data.route)) || {};
  data.settings = data?.page?.linkedFrom?.pageCollection?.items[0] || [];
  if (data?.page?.componentsCollection?.items) {
    data.items = await GetComponentsContentful(
      data?.page?.componentsCollection
    );

    // Delete unnecessary data, lighter response from server to client
    delete data?.page?.componentsCollection;
  }

  return {
    props: { data },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - 1 second: dev
    // - 5 minutes: other envs
    revalidate: 120 // In seconds
  };
};

export default Index;
